import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import useOutsideClick from '../../../../utils/click.utils';
import { useSelectWithParams } from '../../../../utils/selector.utils';
import { IReducedUsers } from '../../../pages/UserPage/UserList/types';
import { reduceUsersByFirstName } from '../../../../utils/user.utils';
import { IUserSelectorMemberProps, IUserSelectorProps } from './types';
import { IUser } from '../../../../app/resource/user/user.types';
import { UserImgWithDelete } from '../../../pages/common/widgets/TaskCard/TaskWidgets/Members/TaskMembers/UserImgWithDelete';
import { CircularAvatar } from '../../../pages/common/CustomAvatar';

export const UserSelector: FunctionComponent<IUserSelectorProps> = ({
  userIds,
  selectedUserIds,
  onSelectionChange,
  closeMenu,
  showSearch = true,
  showSelectAll = true,
  title = 'Select Users',
}) => {
  let users = useSelectWithParams(userSelectors.selectResourcesById, userIds) as IUser[];
  users = users.filter((user) => !user.deleted);

  const [searchParam, setSearchParam] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const reducedUsersByFirstName = useMemo<IReducedUsers>(() => reduceUsersByFirstName(users, searchParam), [
    users,
    searchParam,
  ]);

  const onFilter = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  }, []);

  const handleClickOutside = () => {
    setIsOpen(false);
    if (closeMenu) {
      closeMenu();
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const onSelectAllMembers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const newSelection = checked ? userIds : [];
    onSelectionChange(newSelection);
  };

  const handleUserToggle = useCallback(
    (userId: string) => {
      const isSelected = selectedUserIds.includes(userId);
      if (isSelected) {
        // Remove user from selection
        const newSelection = selectedUserIds.filter((id) => id !== userId);
        onSelectionChange(newSelection);
      } else {
        // Add user to selection
        const newSelection = [...selectedUserIds, userId];
        onSelectionChange(newSelection);
      }
    },
    [selectedUserIds, onSelectionChange],
  );

  const allMembersSelected = selectedUserIds.length === userIds.length;

  const ref = useOutsideClick(handleClickOutside);

  return (
    <div className="flex aic py-1">
      {selectedUserIds.map((userId) => (
        <UserImgWithDelete key={`memberId-${userId}`} userId={userId} onDelete={() => handleUserToggle(userId)} />
      ))}
      <div className={isOpen ? 'dropdown opened' : 'dropdown'}>
        <button
          className="small-avatar gray-bg blueGray bold mr-05 flex aic jcc dropdown-button"
          onClick={toggleDropdown}
        >
          <i className="icon-plus" />
        </button>
        {isOpen && (
          <div className="dropdown-menu" ref={ref}>
            <div className="mini-popup">
              <div className="mini-popup-header">
                {title}
                <span className="close_mini_popup" onClick={toggleDropdown}>
                  ×
                </span>
              </div>
              <div className="mini-popup-body">
                {showSearch && (
                  <form action="" className="form-search-contacts">
                    <input type="text" className="small-input" placeholder="Search users" onChange={onFilter} />
                  </form>
                )}
                {showSelectAll && (
                  <div className="mt-2 mb-2">
                    <input
                      type="checkbox"
                      id="check-all-members"
                      checked={allMembersSelected}
                      onChange={onSelectAllMembers}
                    />
                    <label htmlFor="check-all-members">
                      {allMembersSelected ? 'Deselect all users' : 'Select all users'}
                    </label>
                  </div>
                )}
                <label className="mb-1 mt-1 d-block">Users</label>
                {users && (
                  <div className="list-members">
                    {Object.keys(reducedUsersByFirstName).map((letter) => (
                      <div key={letter}>
                        {reducedUsersByFirstName[letter].map(
                          (user: IUser) =>
                            user && (
                              <UserSelectorMember
                                userId={user.id}
                                key={`memberId-${user.id}`}
                                isSelected={selectedUserIds.includes(user.id)}
                                onToggle={handleUserToggle}
                              />
                            ),
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const UserSelectorMember: FunctionComponent<IUserSelectorMemberProps> = ({ userId, isSelected, onToggle }) => {
  const user = useSelectWithParams(userSelectors.selectResourceById, userId);

  const handleToggle = () => {
    onToggle(userId);
  };

  return (
    user && (
      <div className={isSelected ? 'one-member selected' : 'one-member'} onClick={handleToggle}>
        <CircularAvatar
          src={user.image}
          title={`${user.firstName} ${user.lastName}`}
          classes="small-avatar img-fluid mr-1"
        />
        {`${user.firstName} ${user.lastName}`}
      </div>
    )
  );
};
